import type { AmlCallView, NormalizedPhoneCallDisplay, PhoneCallView } from '../../../phone-calls/types';

export type CallLocationSource = 'GPS' | 'ADDRESS';

/**
 * Build the location search query for an ANI/ALI caller address, mirroring
 * `useCallImport`'s `getPhoneCallLocationQuery` so auto-import resolves the exact
 * same string a manual Import would. When subpremise support is on, the building /
 * floor / unit tokens are appended as ` #`-delimited parts.
 */
export function buildCallAddressQuery(
    call: NormalizedPhoneCallDisplay,
    subpremiseSupport: boolean
): string {
    let display = call.callerAddress ?? '';
    if (subpremiseSupport) {
        display += [
            '',
            call.subscriberBuildingName,
            call.subscriberBuildingFloor,
            call.subscriberUnitNumber,
        ]
            .filter((part) => part !== undefined)
            .join(' #');
    }
    return display;
}

/**
 * Decide whether a caller's location should come from GPS coordinates or from the caller
 * address, matching `useCallImport`'s `importGpsOrAddress` precedence exactly:
 *   1. VOIP + `preferPhysicalAddressForVoip911Calls` + an address → use the address
 *      (the address is more reliable than VOIP tower coordinates).
 *   2. Valid coordinates, or no address at all → use GPS (`"lat,lng"` reverse geocode).
 *   3. Otherwise → use the address.
 *
 * Returns `null` when there is no call, or when the chosen source carries no data — e.g.
 * no coordinates and no address — so callers can skip resolution entirely.
 */
export function chooseCallLocationSource(
    call: PhoneCallView | AmlCallView | undefined,
    opts: { preferPhysicalAddressForVoip911Calls: boolean }
): CallLocationSource | null {
    if (!call) {
        return null;
    }
    const display = call.normalizedPhoneCallDisplay;
    const hasAddress = !!display.callerAddress;

    const preferAddressOverGpsForVoip =
        opts.preferPhysicalAddressForVoip911Calls &&
        display.classOfService === 'VOIP' &&
        hasAddress;
    const noCoordinatesButHasAddress = !display.hasValidLatLng && hasAddress;

    if (preferAddressOverGpsForVoip || noCoordinatesButHasAddress) {
        return 'ADDRESS';
    }
    return display.callerGps ? 'GPS' : null;
}

/**
 * The location search query for a caller, resolved from whichever source
 * `chooseCallLocationSource` selects. `null` when there is nothing to resolve.
 */
export function buildCallLocationQuery(
    call: PhoneCallView | AmlCallView | undefined,
    opts: { preferPhysicalAddressForVoip911Calls: boolean; subpremiseSupport: boolean }
): string | null {
    const source = chooseCallLocationSource(call, opts);
    if (!call || !source) {
        return null;
    }
    const display = call.normalizedPhoneCallDisplay;
    return source === 'ADDRESS'
        ? buildCallAddressQuery(display, opts.subpremiseSupport)
        : display.callerGps ?? null;
}
